import React, { useState } from 'react';
import { CreditCard, Smartphone, Loader2 } from 'lucide-react';
import { PricingPlan, PaymentDetails } from '../../types/profile';

// Logos Mobile Money : source unique
import { OperatorLogo } from './OperatorLogo';

// Logos des cartes bancaires (SVG inline)
const VisaLogo = () => (
  <svg viewBox="0 0 780 500" className="h-6 w-auto">
    <path fill="#1434CB" d="M293.2 348.7l33.4-195.7h53.4l-33.4 195.7zM540.6 157.8c-10.6-4-27.2-8.2-47.9-8.2-52.8 0-90 26.6-90.2 64.7-.3 28.2 26.5 43.9 46.8 53.2 20.8 9.6 27.8 15.7 27.7 24.2-.1 13.1-16.6 19.1-32 19.1-21.4 0-32.7-3-50.3-10.3l-6.9-3.1-7.5 43.8c12.5 5.5 35.6 10.2 59.6 10.5 56.2 0 92.6-26.3 93-67.1.2-22.3-14-39.3-44.8-53.3-18.6-9.1-30.1-15.1-30-24.3 0-8.1 9.7-16.8 30.6-16.8 17.4-.3 30.1 3.5 40 7.5l4.8 2.3 7.1-42.2zM676.5 153h-41.3c-12.8 0-22.4 3.5-28 16.3l-79.3 179.4h56.1s9.2-24.1 11.2-29.4c6.1 0 60.7.1 68.5.1 1.6 6.9 6.5 29.3 6.5 29.3h49.6l-43.3-195.7zm-65.8 126.4c4.4-11.3 21.4-54.8 21.4-54.8-.3.5 4.4-11.4 7.1-18.8l3.6 17s10.3 47 12.5 56.6h-44.6zM232.7 153l-52.3 133.5-5.6-27c-9.7-31.2-40-65-73.8-81.9l47.8 171h56.6l84.2-195.6h-56.9z"/>
    <path fill="#F9A533" d="M131.9 153H45.8l-.7 4.1c67.2 16.3 111.7 55.5 130.2 102.7l-18.8-90.3c-3.2-12.4-12.6-16-24.6-16.5z"/>
  </svg>
);

const MastercardLogo = () => (
  <svg viewBox="0 0 780 500" className="h-6 w-auto">
    <circle cx="250" cy="250" r="150" fill="#EB001B"/>
    <circle cx="530" cy="250" r="150" fill="#F79E1B"/>
    <path fill="#FF5F00" d="M325 130c-45.3 39.4-74 97.1-74 161.5s28.7 122.1 74 161.5c45.3-39.4 74-97.1 74-161.5s-28.7-122.1-74-161.5z"/>
  </svg>
);

const MosoloLogo = () => (
  <div className="h-6 px-2 bg-green-600 text-white rounded flex items-center justify-center text-xs font-bold">
    MOSOLO
  </div>
);

// Constantes pour les méthodes de paiement
const PAYMENT_METHODS = [
  {
    id: 'card' as const,
    name: 'Carte bancaire / Mosolo',
    description: 'Visa, MasterCard, Mosolo',
    icon: CreditCard
  },
  {
    id: 'mobile' as const,
    name: 'Mobile Money',
    description: 'Airtel Money, M-Pesa, Orange Money',
    icon: Smartphone
  }
];

// Cartes acceptées avec logos SVG
const ACCEPTED_CARDS = [
  { name: 'VISA', Logo: VisaLogo },
  { name: 'Mastercard', Logo: MastercardLogo },
  { name: 'Mosolo', Logo: MosoloLogo }
] as const;

/**
 * Fournisseurs Mobile Money avec codes télécom backend
 * Conformes à la documentation: AM (Airtel), OM (Orange), MP (M-Pesa), AF (Africell)
 */
const MOBILE_MONEY_PROVIDERS = [
  { id: 'airtel', telecomCode: 'AM', name: 'Airtel Money', ussd: '*150#' },
  { id: 'mpesa', telecomCode: 'MP', name: 'M-Pesa', ussd: '*100#' },
  { id: 'orange', telecomCode: 'OM', name: 'Orange Money', ussd: '#150#' }
] as const;

/**
 * Pays supportés avec codes téléphoniques
 * Format numéro attendu par le backend: 243XXXXXXXXX (12 chiffres)
 */
const COUNTRIES = [
  { code: '243', name: 'RD Congo', flag: '🇨🇩', phoneLength: 9 },
  { code: '250', name: 'Rwanda', flag: '🇷🇼', phoneLength: 9 },
  { code: '256', name: 'Uganda', flag: '🇺🇬', phoneLength: 9 },
  { code: '255', name: 'Tanzania', flag: '🇹🇿', phoneLength: 9 },
  { code: '254', name: 'Kenya', flag: '🇰🇪', phoneLength: 9 }
];

// Périodes de facturation avec réductions
const BILLING_PERIODS = [
  {
    id: 'monthly' as const,
    name: 'Mensuel',
    multiplier: 1,
    discount: 0
  },
  {
    id: 'annual' as const,
    name: 'Annuel',
    multiplier: 12,
    discount: 0.15 // 15% de réduction
  }
];

// Codes promo prédéfinis
const PROMO_CODES = {
  'WELCOME10': 0.10,
  'SAVE20': 0.20,
  'NEWUSER': 0.15,
  'ANNUAL25': 0.25
};

// Valeurs par défaut
export const DEFAULT_PAYMENT_TYPE = 'card';
export const DEFAULT_COUNTRY_CODE = '+243';

interface PaymentModalProps {
  plan: PricingPlan | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  paymentType: 'card' | 'mobile' | 'manual';
  setPaymentType: (type: 'card' | 'mobile' | 'manual') => void;
  paymentInfo: PaymentDetails;
  setPaymentInfo: (info: PaymentDetails) => void;
  error: string;
  billingPeriod: 'monthly' | 'annual';
  setBillingPeriod: (period: 'monthly' | 'annual') => void;
  promoCode: string;
  setPromoCode: (code: string) => void;
  promoDiscount: number;
  onValidatePromo: (code: string) => Promise<number> | number; // 🆕 Support async
  onSetPromoDiscount: (discount: number) => void;
  isValidatingPromo?: boolean; // 🆕 État de chargement externe
}

export default function PaymentModal({
  plan, 
  isOpen, 
  onClose, 
  onSubmit,
  paymentType,
  setPaymentType,
  paymentInfo,
  setPaymentInfo,
  error,
  billingPeriod,
  setBillingPeriod,
  promoCode,
  setPromoCode,
  promoDiscount,
  onValidatePromo,
  onSetPromoDiscount,
  isValidatingPromo = false
}: PaymentModalProps) {
  // 🆕 État local pour le chargement de validation promo
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  
  if (!isOpen || !plan) return null;

  // Calcul du prix avec période et réductions
  const selectedBillingPeriod = BILLING_PERIODS.find(p => p.id === billingPeriod)!;
  const basePrice = parseFloat(plan.price) || 0;
  const periodPrice = basePrice * selectedBillingPeriod.multiplier;
  const periodDiscount = periodPrice * selectedBillingPeriod.discount;
  const promoDisountAmount = (periodPrice - periodDiscount) * promoDiscount;
  const finalPrice = periodPrice - periodDiscount - promoDisountAmount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl p-4 sm:p-6 lg:p-8 w-full max-w-md sm:max-w-lg lg:max-w-xl mx-auto max-h-[95vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
            Paiement - {plan.name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl sm:text-2xl p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="mb-4 sm:mb-6 p-3 sm:p-4 lg:p-5 bg-gray-50 rounded-xl">
          {/* Sélection de la période */}
          <div className="mb-4 sm:mb-5">
            <label className="block text-sm sm:text-base font-medium text-gray-700 mb-2 sm:mb-3">
              Période de facturation
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {BILLING_PERIODS.map((period) => (
                <label key={period.id} className={`relative flex items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  billingPeriod === period.id 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="billingPeriod"
                    value={period.id}
                    checked={billingPeriod === period.id}
                    onChange={() => setBillingPeriod(period.id)}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="font-medium text-gray-900 text-sm sm:text-base">{period.name}</div>
                    {period.discount > 0 && (
                      <div className="text-xs text-green-600 font-medium">
                        -{Math.round(period.discount * 100)}%
                      </div>
                    )}
                  </div>
                  {period.discount > 0 && (
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                      Promo
                    </div>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Résumé des prix */}
          <div className="space-y-2 sm:space-y-3">
            <div className="flex justify-between text-sm sm:text-base">
              <span>Prix de base ({selectedBillingPeriod.name.toLowerCase()})</span>
              <span className="font-medium">${periodPrice.toFixed(2)}</span>
            </div>
            {selectedBillingPeriod.discount > 0 && (
              <div className="flex justify-between text-sm sm:text-base text-green-600">
                <span>Réduction annuelle (-{Math.round(selectedBillingPeriod.discount * 100)}%)</span>
                <span className="font-medium">-${periodDiscount.toFixed(2)}</span>
              </div>
            )}
            {promoDiscount > 0 && (
              <div className="flex justify-between text-sm sm:text-base text-green-600">
                <span>Code promo (-{Math.round(promoDiscount * 100)}%)</span>
                <span className="font-medium">-${promoDisountAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t pt-3 sm:pt-4">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-900 text-base sm:text-lg">Total</span>
                <div className="text-right">
                  <div className="text-2xl sm:text-3xl font-bold text-primary">${finalPrice.toFixed(2)}</div>
                  <div className="text-xs sm:text-sm text-gray-600">
                    {billingPeriod === 'annual' ? 'par an' : 'par mois'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4 sm:space-y-5">
          {/* Sélection de la méthode de paiement */}
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700 mb-3">
              Méthode de paiement
            </label>
            <div className="grid grid-cols-1 gap-3">
              {PAYMENT_METHODS.map((method) => {
                const IconComponent = method.icon;
                return (
                  <label key={method.id} className={`flex items-center p-3 sm:p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                    paymentType === method.id 
                      ? 'border-primary bg-primary/5 shadow-sm' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.id}
                      checked={paymentType === method.id}
                      onChange={() => setPaymentType(method.id)}
                      className="sr-only"
                    />
                    <div className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-lg mr-3 sm:mr-4 ${
                      paymentType === method.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <IconComponent className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm sm:text-base">{method.name}</div>
                      {method.id === 'mobile' ? (
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {MOBILE_MONEY_PROVIDERS.map((p) => <OperatorLogo key={p.id} operator={p.telecomCode} size={22} />)}
                        </div>
                      ) : (
                        <div className="text-xs sm:text-sm text-gray-500 truncate">{method.description}</div>
                      )}
                    </div>
                    {paymentType === method.id && (
                      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 ml-2">
                        <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-white rounded-full"></div>
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Code promo */}
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700 mb-2">
              Code de promotion (optionnel)
            </label>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <input
                type="text"
                placeholder="Entrez votre code promo"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="flex-1 p-3 sm:p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
              />
              <button
                type="button"
                disabled={validatingPromo || isValidatingPromo}
                onClick={async () => {
                  if (promoCode.trim()) {
                    setValidatingPromo(true);
                    setPromoError(null);
                    try {
                      const discount = await Promise.resolve(onValidatePromo(promoCode.trim()));
                      onSetPromoDiscount(discount);
                      if (discount === 0) {
                        setPromoError('Code promo invalide ou expiré');
                      }
                    } catch (err) {
                      setPromoError('Erreur de validation');
                      onSetPromoDiscount(0);
                    } finally {
                      setValidatingPromo(false);
                    }
                  }
                }}
                className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm sm:text-base font-medium disabled:opacity-50 min-w-[100px] sm:min-w-[110px] flex items-center justify-center"
              >
                {validatingPromo || isValidatingPromo ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                ) : (
                  'Appliquer'
                )}
              </button>
            </div>
            {promoError && (
              <p className="text-xs sm:text-sm text-red-600 mt-1.5">
                ✗ {promoError}
              </p>
            )}
            {promoDiscount > 0 && (
              <p className="text-xs sm:text-sm text-green-600 mt-1.5 flex items-center">
                ✓ Code promo appliqué - {Math.round(promoDiscount * 100)}% de réduction
              </p>
            )}
          </div>

          {/* Champs selon la méthode */}
          {paymentType === 'card' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Informations de la carte
                </label>
                
                {/* Logos des cartes acceptées */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 p-3 sm:p-4 bg-gray-50 rounded-xl">
                  <span className="text-xs sm:text-sm text-gray-600">Cartes acceptées:</span>
                  <div className="flex items-center gap-2 sm:gap-3">
                    {ACCEPTED_CARDS.map((card) => (
                      <div key={card.name} className="flex items-center" title={card.name}>
                        <card.Logo />
                      </div>
                    ))}
                  </div>
                </div>
                
                <input
                  type="text"
                  placeholder="Numéro de carte (Visa/MasterCard/Mosolo)"
                  value={paymentInfo.cardNumber}
                  onChange={(e) => setPaymentInfo({...paymentInfo, cardNumber: e.target.value})}
                  className="w-full p-3 sm:p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <input
                  type="text"
                  placeholder="MM/AA"
                  value={paymentInfo.expiryDate}
                  onChange={(e) => setPaymentInfo({...paymentInfo, expiryDate: e.target.value})}
                  className="p-3 sm:p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                  required
                />
                <input
                  type="text"
                  placeholder="CVV"
                  value={paymentInfo.cvv}
                  onChange={(e) => setPaymentInfo({...paymentInfo, cvv: e.target.value})}
                  className="p-3 sm:p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                  required
                />
              </div>
            </div>
          )}

          {paymentType === 'mobile' && (
            <div className="space-y-4 sm:space-y-5">
              <div>
                <label className="block text-sm sm:text-base font-medium text-gray-700 mb-3">
                  Choisir votre opérateur
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                  {MOBILE_MONEY_PROVIDERS.map((provider) => (
                    <label key={provider.id} className={`flex items-center justify-center p-3 sm:p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                      paymentInfo.mobileMoneyProvider === provider.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="mobileProvider"
                        value={provider.id}
                        checked={paymentInfo.mobileMoneyProvider === provider.id}
                        onChange={(e) => setPaymentInfo({...paymentInfo, mobileMoneyProvider: e.target.value})}
                        className="sr-only"
                      />
                      <OperatorLogo operator={provider.telecomCode} size={36} decorative className="mr-2 sm:mr-3" />
                      <span className="min-w-0 break-words font-medium text-gray-900 text-sm sm:text-base">{provider.name}</span>
                      {paymentInfo.mobileMoneyProvider === provider.id && (
                        <div className="ml-auto w-5 h-5 sm:w-6 sm:h-6 bg-primary rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-white rounded-full"></div>
                        </div>
                      )}
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-sm sm:text-base font-medium text-gray-700 mb-2">
                    Numéro de téléphone
                  </label>
                  <div className="flex">
                    <select 
                      value={paymentInfo.countryCode || DEFAULT_COUNTRY_CODE}
                      onChange={(e) => setPaymentInfo({...paymentInfo, countryCode: e.target.value})}
                      className="rounded-l-xl border border-gray-300 border-r-0 bg-gray-50 px-3 py-3 sm:py-4 focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    >
                      {COUNTRIES.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <div className="relative min-w-0 flex-1">
                      {paymentInfo.mobileMoneyProvider && (
                        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                          <OperatorLogo operator={paymentInfo.mobileMoneyProvider} size={24} />
                        </span>
                      )}
                      <input
                        type="tel"
                        placeholder="820123456"
                        value={paymentInfo.mobileNumber}
                        onChange={(e) => setPaymentInfo({...paymentInfo, mobileNumber: e.target.value})}
                        className={`w-full min-w-0 rounded-r-xl border border-gray-300 border-l-0 p-3 sm:p-4 focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base ${paymentInfo.mobileMoneyProvider ? '!pl-11' : ''}`}
                        required
                      />
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1.5">
                    Format: numéro sans le code pays (ex: 820123456)
                  </p>
                </div>
                
                {/* 📱 Message explicatif - Le PIN est géré par l'opérateur via USSD */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4">
                  <p className="text-xs sm:text-sm text-blue-700">
                    <span className="font-medium">📱 Confirmation sécurisée :</span><br/>
                    Après validation, vous recevrez une demande de confirmation sur votre téléphone 
                    via votre opérateur Mobile Money. Entrez votre code PIN sur votre téléphone pour confirmer.
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm sm:text-base">
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-4 sm:pt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 sm:px-6 py-3 sm:py-3.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium text-sm sm:text-base transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 px-4 sm:px-6 py-3 sm:py-3.5 bg-primary text-white rounded-xl hover:bg-primary-dark font-semibold text-sm sm:text-base transition-colors shadow-lg shadow-primary/20"
            >
              Payer ${finalPrice.toFixed(2)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Export des types et constantes nécessaires
export { PROMO_CODES };